<template>
  <div class="team-presence-table do-scroller__container">
    <div class="do-scroller do-scroller-max-height">
      <table class="team-presence-table__table">
        <thead>
        <tr>
          <th class="team-presence-table__col-name">{{ i18n[locale]['employee'] }}</th>
          <th class="team-presence-table__col-total">{{ i18n[locale]['week_total'] }}</th>
          <th
            v-for="day in weekDays"
            :key="day.format('YYYY-MM-DD')"
            class="team-presence-table__col-day">
            <span class="team-presence-table__day-name">{{ formatDayName(day) }}</span>
            <span class="team-presence-table__day-date">{{ formatDayDate(day) }}</span>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="row in tableRows"
          :key="row.calendarId"
          class="team-presence-table__row">
          <td class="team-presence-table__col-name">{{ row.name }}</td>
          <td class="team-presence-table__col-total">{{ row.weekTotal }}</td>
          <td
            v-for="day in weekDays"
            :key="row.calendarId + '-' + day.format('YYYY-MM-DD')"
            class="team-presence-table__col-day">
            <div
              v-for="(slot, idx) in row.slotsByDay[day.format('YYYY-MM-DD')]"
              :key="row.calendarId + '-' + day.format('YYYY-MM-DD') + '-' + idx"
              class="team-presence-table__slot">
              {{ slot }}
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

const I18N = {
  da: {
    employee: 'Medarbejder',
    week_total: 'Ugetotal'
  },
  en: {
    employee: 'Employee',
    week_total: 'Week total'
  },
  nb: {
    employee: 'Ansatt',
    week_total: 'Uketotal'
  }
}

export default {
  props: {
    calendars: {
      type: Array,
      default: () => []
    },
    events: {
      type: Array,
      default: () => []
    },
    date: {
      required: true
    },
    locale: {
      type: String,
      default: 'da'
    },
    isShowWeekends: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18n: I18N
    }
  },
  computed: {
    weekStart () {
      return moment(this.date).isoWeekday(1).startOf('day')
    },
    weekDays () {
      const count = this.isShowWeekends ? 7 : 5
      const days = []
      for (let i = 0; i < count; i++) {
        days.push(moment(this.weekStart).add(i, 'days'))
      }
      return days
    },
    presenceEvents () {
      const calendarIds = this.calendars.map(calendar => calendar.id)
      return this.events.filter(event => {
        if (!event || event.event_type !== 'presence') {
          return false
        }
        const calId = event.calendar_id
        if (calId && calendarIds.indexOf(calId) > -1) {
          return true
        }
        if (event.calendars && event.calendars.length > 0) {
          return event.calendars.some(calendar => calendarIds.indexOf(calendar.calendar_id) > -1)
        }
        if (event.calendar_events && event.calendar_events.length > 0) {
          return event.calendar_events.some(calendar => calendarIds.indexOf(calendar.calendar_id) > -1)
        }
        return false
      })
    },
    tableRows () {
      return this.calendars.map(calendar => {
        const calendarPresence = this.presenceEvents.filter(event => this.getEventCalendarId(event) === calendar.id)
        const weekTotalMinutes = calendarPresence.reduce((sum, event) => {
          return sum + moment(event.end).diff(moment(event.start), 'minutes')
        }, 0)
        const slotsByDay = {}
        this.weekDays.forEach(day => {
          const dayKey = day.format('YYYY-MM-DD')
          slotsByDay[dayKey] = calendarPresence
            .filter(event => moment(event.start).format('YYYY-MM-DD') === dayKey)
            .sort((a, b) => moment(a.start).unix() - moment(b.start).unix())
            .map(event => this.formatTimeRange(event))
        })
        return {
          calendarId: calendar.id,
          name: calendar.name,
          weekTotal: this.formatMinutesAsHHMM(weekTotalMinutes),
          slotsByDay
        }
      })
    }
  },
  methods: {
    getEventCalendarId (event) {
      if (event.calendar_id) {
        return event.calendar_id
      }
      if (event.calendars && event.calendars.length > 0) {
        return event.calendars[0].calendar_id
      }
      if (event.calendar_events && event.calendar_events.length > 0) {
        return event.calendar_events[0].calendar_id
      }
      return null
    },
    formatTimeRange (event) {
      return `${this.$filters.LT(event.start)} - ${this.$filters.LT(event.end)}`
    },
    formatMinutesAsHHMM (minutes) {
      const total = Math.max(0, Math.round(minutes))
      const hours = Math.floor(total / 60)
      const mins = total % 60
      return `${hours}:${String(mins).padStart(2, '0')}`
    },
    formatDayName (day) {
      return moment(day).locale(this.locale).format('dddd')
    },
    formatDayDate (day) {
      const m = moment(day).locale(this.locale)
      if (this.locale === 'en') {
        return m.format('MM/DD')
      }
      return m.format('DD[/]MM')
    }
  }
}
</script>

<style scoped>
.do-scroller__container {
  overflow: hidden;
}

.team-presence-table {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

.do-scroller {
  width: 100%;
  overflow: auto;
}

.do-scroller-max-height {
  max-height: calc(97vh - 220px);
}

.team-presence-table__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  color: #3d4340;
}

/* Override simplegrid [class*='col-'] on team-presence-table__col-* cells */
.team-presence-table__table th[class*='col-'],
.team-presence-table__table td[class*='col-'] {
  float: none;
  min-height: auto;
  padding-right: 0.75rem;
}

.team-presence-table__table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 0.65rem 0.75rem;
  text-align: left;
  font-weight: 700;
  white-space: nowrap;
}

.team-presence-table__row:nth-child(even) {
  background: #f4f6f8;
}

.team-presence-table__row:nth-child(odd) {
  background: #ffffff;
}

.team-presence-table__table td {
  border-bottom: 1px solid #e8edf2;
  padding: 0.55rem 0.75rem;
  vertical-align: top;
}

.team-presence-table__col-name {
  min-width: 140px;
  font-weight: 600;
  white-space: nowrap;
}

.team-presence-table__col-total {
  min-width: 72px;
  white-space: nowrap;
}

.team-presence-table__col-day {
  min-width: 110px;
  white-space: normal;
}

.team-presence-table__col-day .team-presence-table__day-name,
.team-presence-table__col-day .team-presence-table__day-date {
  display: block;
}

.team-presence-table__day-name {
  font-weight: 700;
  text-transform: capitalize;
  line-height: 1.25;
}

.team-presence-table__day-date {
  margin-top: 0.2rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.2;
}

.team-presence-table__slot {
  line-height: 1.35;
  white-space: nowrap;
}

.team-presence-table__slot + .team-presence-table__slot {
  margin-top: 0.15rem;
}
</style>



// WEBPACK FOOTER //
// TeamCalendarPresenceTable.vue?5afd7fa0